import { useState } from "react";
import { usePanes } from "../../main/panes/usePanes";
import AddLegalEntityPane from "../addLegalEntityPane/AddLegalEntityPane";
import { ITableHeader, useDotLegalSnackbar, useHistoryWithReferer } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";
import { useStateSessionStorage } from "@common/hooks/useStateSessionStorage.ts";
import { useDotLegalPostGetQueryArray } from "@common/hooks/dotLegalReactQuery";
import { FilterOption } from "@common/components/filterPopover/FilterPopover.types.ts";
import { withoutFilterPresentationState } from "@common/components/filterBar/filterPinning.ts";
import { withFilterOperators } from "@common/components/filterBar/filterOperatorHydration.ts";
import { usePageView } from "@common/pageView/PageView.hooks.ts";
import { FilterPage, SessionStorageSavedView } from "@common/pageView/PageView.types.ts";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks.ts";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types.ts";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport.ts";
import {
    LegalEntityExportHeader,
    LegalEntityFilterItem,
    LegalEntityFilterRequest,
    LegalEntityScope,
    LegalEntitySearchResult,
    LegalEntitySortDirection,
    toLegalEntityScope,
} from "./LegalEntities.types";
import { getLegalEntityFilterIdByColumn, LegalEntityColumn, legalEntityColumnByFilterId, toLegalEntityDisplayFields } from "./legalEntityColumns";
import { Guid } from "@common/guid";
import { defaultLegalEntityPageSize, transformLegalEntityFilterOptionsToBackend } from "./LegalEntityFilter.types.ts";
import useLegalEntityDisplayFields from "./legalEntityDisplayFields/LegalEntityDisplayFields.hooks.ts";
import { useLegalEntityFilter } from "./LegalEntityFilter.hooks.ts";

export { defaultLegalEntityPageSize } from "./LegalEntityFilter.types.ts";

export interface ILegalEntitiesProps {
    scope: LegalEntityScope;
}

export function useLegalEntitiesHook(props: ILegalEntitiesProps) {
    const { translateString } = useTranslation();
    const { getLegalEntityBaseUrl, getLegalEntityEditUrl: getTemplateEditUrl } = useUrlProvider();
    const historyWithReferer = useHistoryWithReferer();
    const snackBar = useDotLegalSnackbar();
    const { exportLegalEntitiesInBackground } = useCustomListExport();
    const { addonsFeatures, vendorManagementFeatures } = usePlanContext();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const { customerId, permissions } = useUserContext();

    const isGroupEntities = props.scope === LegalEntityScope.GroupEntity;
    const filterPage = isGroupEntities ? FilterPage.LegalEntityGroupCompanies : FilterPage.LegalEntityExternal;

    const { tableFields, getTableFieldsDictionary, isLoading: isFetchingDisplayFields } = useLegalEntityDisplayFields(props.scope);

    // Search stays out of the saved view blob. It is a transient way to find one row, not part of what a view
    // describes, and keeping it out is also what lets the blob deep-equal the backend default on a cold load.
    const [searchText, setSearchText] = useStateSessionStorage<string>(`legalEntitiesSearch:${customerId}:${props.scope}`, "");
    const { open: openPane } = usePanes();
    // Null while closed; the report kind doubles as the open flag so the two cannot disagree.
    const [openReportType, setOpenReportType] = useState<"audit" | "classification" | null>(null);

    const savedViewQuery = useSavedViewQuery(filterPage);
    const {
        currentViewId,
        getFilterData,
        isSavedView,
        setFilterData,
        setDisplayData,
        getDisplayData,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        setView,
        savedView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView(filterPage, savedViewQuery.data);

    // Must match GetDefaultFilterData on LegalEntitySavedViewStrategyBase key for key, or a cold load reports the
    // system default view as changed. Only isActive and showOnlyMine count towards that comparison; PageView
    // excludes the rest.
    const defaultFilter: Partial<LegalEntityFilterRequest> = {
        isActive: true,
        showOnlyMine: false,
        pageNumber: 1,
        pageSize: defaultLegalEntityPageSize,
        sortByColumn: LegalEntityColumn.UserFriendlyId,
        sortDirection: LegalEntitySortDirection.ASC,
    };

    const filterData = getFilterData<Partial<LegalEntityFilterRequest>>() ?? defaultFilter;
    // The pins and operators ride inside the blob so they travel with a saved view, but the server must not see
    // them: without this, pinning an already-empty filter would change the query key and refetch the list.
    const queryFilterData = withoutFilterPresentationState(filterData);

    const tableFieldsDictionary = getTableFieldsDictionary();
    const selectedTableColumns = getDisplayData<Array<string>>() ?? [];
    // A view saved while a plan or permission was active can name columns this user may no longer see, and a
    // deleted custom field never reaches the dictionary at all - either would otherwise head a column with its
    // raw id. Dropping them here is what the removed getSelectedColumns() used to guarantee by construction.
    const visibleColumns = selectedTableColumns.filter((columnId) => tableFieldsDictionary[columnId] !== undefined);

    const headers: Array<ITableHeader<LegalEntityFilterItem>> = visibleColumns.map((columnId) => ({
        property: columnId,
        text: tableFieldsDictionary[columnId]!.translation,
        tooltip: tableFieldsDictionary[columnId]!.tooltip,
        align: "left",
        showOnMobile: true,
    }));

    const { legalEntityFilterOptions, isLoading: isLoadingFilters } = useLegalEntityFilter(props.scope, filterData);

    const filter = {
        ...queryFilterData,
        scope: props.scope,
        searchText: searchText || undefined,
    };

    const { data, isLoading, refetch } = useDotLegalPostGetQueryArray<LegalEntitySearchResult>(
        ["/legal-entities/search", filter, visibleColumns],
        "/legal-entities/search",
        { filter: filter, displayFields: toLegalEntityDisplayFields(visibleColumns) },
        // The custom field names have to be known before the table renders, or a custom field column heads up with
        // its bare guid until the dictionary arrives.
        { enabled: !savedViewQuery.isLoading && !isFetchingDisplayFields }
    );

    const onFilterBarUpdate = (filterOptions: Array<FilterOption>) => {
        setFilterData(
            withFilterOperators(transformLegalEntityFilterOptionsToBackend(filterOptions, filterData?.sortByColumn, filterData?.sortDirection), filterOptions)
        );
    };

    const onViewSaved = (viewId: string) => {
        setView({ page: filterPage, viewId: viewId, filterData: filterData, displayData: selectedTableColumns });
    };

    const onViewDeleted = (deletedViewId: string) => {
        if (deletedViewId !== currentViewId) {
            return;
        }

        const fallbackView = savedViewQuery.data?.find((x) => x.isCustomerDefault || x.isSystemDefault);
        const updatedView: SessionStorageSavedView = {
            page: filterPage,
            viewId: fallbackView?.id === deletedViewId ? undefined : fallbackView?.id,
            filterData: filterData,
            displayData: selectedTableColumns,
        };
        setView(updatedView);
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            setView({ page: filterPage, viewId: view.id, filterData: view.filterData, displayData: view.displayData });
        }
    };

    // A custom field is sorted by its id, and the two are mutually exclusive: leaving sortByColumn set would
    // have the backend fall back to it and silently sort by the wrong thing.
    function onSortChange(columnId: string, direction: "asc" | "desc") {
        const isCustomField = Guid.isGuid(columnId);

        setFilterData({
            ...filterData,
            sortByColumn: isCustomField ? undefined : (legalEntityColumnByFilterId[columnId] ?? LegalEntityColumn.Name),
            sortByCustomFieldId: isCustomField ? columnId : undefined,
            sortDirection: direction === "desc" ? LegalEntitySortDirection.DESC : LegalEntitySortDirection.ASC,
            pageNumber: 1,
        });
    }

    function setPageNumber(page: number) {
        setFilterData({ ...filterData, pageNumber: page });
    }

    function onSearchChange(value: string) {
        setSearchText(value);
        setFilterData({ ...filterData, pageNumber: 1 });
    }

    // Headers and displayColumns are both built from visibleColumns so the Function's rendered Fields line up
    // with the header row by position. Scope and search live outside the blob and are added the same way the
    // page query adds them, so the export returns exactly what the table shows. Paging is only filled in to
    // satisfy the request type: the export Function replaces it with a single unbounded page.
    async function onDownloadExcelReportClick() {
        const headers: Array<LegalEntityExportHeader> = visibleColumns.map((columnId) => ({
            header: tableFieldsDictionary[columnId]!.translation,
            column: Guid.isGuid(columnId) ? undefined : legalEntityColumnByFilterId[columnId],
            customFieldId: Guid.isGuid(columnId) ? columnId : undefined,
        }));

        await exportLegalEntitiesInBackground(savedView!.name, headers, {
            filter: {
                ...filter,
                pageNumber: 1,
                pageSize: filter.pageSize ?? defaultLegalEntityPageSize,
                sortDirection: filter.sortDirection ?? LegalEntitySortDirection.ASC,
            },
            displayFields: toLegalEntityDisplayFields(visibleColumns),
        });
        snackBar.show(translateString("legalEntityDataExportInProgress"), "success", true);
    }

    // The two lists are the two scopes, so every row on a list shares the list's own scope.
    const legalEntityScope = toLegalEntityScope(isGroupEntities);

    function openCreateLegalEntity() {
        openPane(AddLegalEntityPane, { legalEntityScope });
    }

    function getLegalEntityOverviewUrl(row: LegalEntityFilterItem) {
        return getLegalEntityBaseUrl(legalEntityScope, row.id);
    }

    function getLegalEntityEditUrl(row: LegalEntityFilterItem) {
        return getTemplateEditUrl(legalEntityScope, row.id);
    }

    function onGoToLegalEntityClick(row: LegalEntityFilterItem) {
        historyWithReferer.push(getLegalEntityOverviewUrl(row));
    }

    // Read off the row rather than off its cells, so the marking survives a display configuration that leaves the
    // column out. The flag is stored on every entity but only means anything on the group company page.
    function isPreselectedGroupEntity(row: LegalEntityFilterItem) {
        return isGroupEntities && row.isPreselectedOnCreate;
    }

    // Audits go out to external parties, so the group company list offers the classification report only, the
    // way the pre-split lists did.
    const reportMenuItems = vendorManagementFeatures.hasVendorManagement
        ? [
              { children: translateString("legalEntityRiskClassificationReport"), onClick: () => setOpenReportType("classification") },
              ...(isGroupEntities ? [] : [{ children: translateString("legalEntityRiskAuditReport"), onClick: () => setOpenReportType("audit") }]),
          ]
        : [];

    function closeReportDialog() {
        setOpenReportType(null);
    }

    const rows = data?.legalEntities ?? [];
    const isSearch = isSavedView || hasFilterChanged || !!searchText;
    const breadCrumbs = [{ name: isGroupEntities ? translateString("groupCompanies") : translateString("external") }];
    const emptyStateText = isSearch
        ? translateString(isGroupEntities ? "noGroupCompaniesMatchesQuery" : "noLegalEntitiesMatchesQuery")
        : translateString(isGroupEntities ? "noGroupEntities" : "noLegalEntities");

    return {
        isGroupEntities,
        filterPage,
        isLoading: isLoading || savedViewQuery.isLoading || isFetchingDisplayFields,
        isLoadingFilters,
        isLoadingPage: isLoading || savedViewQuery.isLoading || isFetchingDisplayFields || isLoadingFilters,
        legalEntityFilterOptions,
        headers,
        tableFields,
        rows,
        hasData: isLoading || savedViewQuery.isLoading || isFetchingDisplayFields || rows.length > 0,
        isSearch,
        emptyStateText,
        totalCount: data?.totalCount ?? 0,
        totalPages: data?.totalPages ?? 1,
        pageNumber: filterData.pageNumber ?? 1,
        setPageNumber,
        pageSize: data?.pageSize ?? defaultLegalEntityPageSize,
        // The table works in column ids while the blob stores the enum, so the sort is mapped back for the
        // header. A custom field is already an id, so it is handed straight back.
        sortByColumnId: filterData.sortByCustomFieldId ?? getLegalEntityFilterIdByColumn(filterData.sortByColumn ?? LegalEntityColumn.Name) ?? "",
        sortDirection: filterData.sortDirection ?? LegalEntitySortDirection.ASC,
        onSortChange,
        searchText,
        onSearchChange,
        breadCrumbs,
        selectedColumns: visibleColumns,
        filterData,
        setDisplayData,
        onFilterBarUpdate,
        clearFilterData,
        hasFilterChanged,
        hasDisplayChanged,
        isSavedView,
        pinnedFilterIds,
        setPinnedFilterIds,
        savedView,
        currentViewId,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getLegalEntityOverviewUrl,
        getLegalEntityEditUrl,
        legalEntityScope,
        onGoToLegalEntityClick,
        isPreselectedGroupEntity,
        openCreateLegalEntity,
        reportMenuItems,
        openReportType,
        closeReportDialog,
        refetch,
        permissions,
        addonsFeatures,
        hasEnterpriseRiskManagementAccess,
    };
}
